import { motion } from "framer-motion";
import { pct as scoreToPct } from "@/lib/gauge";

const HEIGHTS = { sm: "h-2.5", md: "h-3.5", lg: "h-4" };
const MARKER = { sm: "h-5 w-5", md: "h-6 w-6", lg: "h-7 w-7" };
// A gutter one marker-radius wide on each side, added with a negative margin so
// the track keeps the width it had. It buys two things: the marker stays whole
// at scores of exactly -10 and +10, where half of it would otherwise sit outside
// the box, and it gives the clip below somewhere to cut that isn't through the
// marker.
const GUTTER = {
  sm: { pad: "-mx-2.5 px-2.5", rail: "inset-x-2.5" },
  md: { pad: "-mx-3 px-3", rail: "inset-x-3" },
  lg: { pad: "-mx-3.5 px-3.5", rail: "inset-x-3.5" },
};

/** The size every unrecognised `size` falls back to. */
const DEFAULT_SIZE = "md";

export default function LeanGauge({ score = 0, label = "Center", confidence, size = "md", showScale = true }) {
  const pct = scoreToPct(score);
  // A size this component doesn't know used to be fatal, not cosmetic: `gutter`
  // came back undefined and the very next line read `gutter.pad`, which throws
  // during render and takes the entire route down through the ErrorBoundary.
  // A gauge is a figure on a page; it does not get to decide the page is gone.
  const key = GUTTER[size] ? size : DEFAULT_SIZE;
  const gutter = GUTTER[key];

  // Shown only when there is a number to show. Number(null).toFixed(1) is the
  // string "NaN", and the page printed it: "score NaN", beside a marker parked
  // at centre. Saying nothing is the honest version of not knowing.
  const numericScore = Number(score);
  const hasScore = Number.isFinite(numericScore);

  return (
    <div className="w-full" data-testid="lean-gauge">
      {/* overflow-x-clip, not hidden: a full-width rail translated by up to 100%
          of itself ends up twice as wide as its parent, and that overhang was
          adding 207px of horizontal scroll to every page with a gauge on it.
          `clip` cuts it without creating a scroll container, and unlike `hidden`
          it leaves the other axis genuinely visible — which this needs, because
          the marker is taller than the track and has to spill vertically. */}
      <div className={`relative overflow-x-clip ${gutter.pad}`}>
        <div
          className={`w-full rounded-full ${HEIGHTS[key]}`}
          // Blue-to-red is the reading, so it stays fixed in both themes. Only
          // the centre stop moves: "neither side" has to look like the absence
          // of a tint, and a slate-200 band glows on a dark page.
          style={{ background: "linear-gradient(90deg,#2563eb 0%,#93c5fd 30%,var(--c-gauge-mid) 50%,#fca5a5 70%,#dc2626 100%)" }}
        />
        {/* The rail is what moves, not the marker. A percentage translate
            resolves against the element's OWN width, so translating a 24px
            marker by 75% would shift it 18px rather than three quarters of the
            way along the bar. The rail spans the track, so its percentage and
            the score's percentage are the same number.

            `left` did read correctly, but it is a layout property: animating it
            re-ran layout, paint and composite on every frame of the one figure
            the page exists to show.

            `x`, not a `transform` string — framer applies a raw transform string
            once and never interpolates it, which parks the marker on its initial
            value. */}
        <motion.div
          className={`pointer-events-none absolute top-1/2 ${gutter.rail}`}
          initial={{ x: "50%", opacity: 0 }}
          animate={{ x: `${pct}%`, opacity: 1 }}
          // bounce 0, not the previous stiffness 90 / damping 16 — a damping
          // ratio of 0.84 is underdamped, so the marker travelled a little past
          // the score and came back. Overshoot is fine on a drawer; on a
          // measurement it means briefly displaying a reading that was never
          // taken.
          transition={{ type: "spring", duration: 0.5, bounce: 0, delay: 0.15 }}
        >
          <span
            className={`absolute left-0 top-0 ${MARKER[key]} -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-paper bg-ink shadow-md`}
            data-testid="lean-gauge-marker"
          />
        </motion.div>
      </div>
      {showScale && (
        <div className="mt-2 flex justify-between text-[11px] font-medium uppercase tracking-wide text-faint">
          <span>Left</span>
          <span>Center</span>
          <span>Right</span>
        </div>
      )}
      {/* flex-wrap so a narrow gauge breaks between the three readings rather
          than through the middle of one — without it "score 0.4" split across
          two lines inside the peek dialog on a phone. Nothing wraps at the
          widths this sits at on desktop, so nothing there moves. */}
      <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-serif text-2xl font-bold text-ink" data-testid="lean-gauge-label">{label}</span>
        {hasScore && <span className="text-sm text-faint">score {numericScore.toFixed(1)}</span>}
        {confidence != null && <span className="text-sm text-faint">· {confidence}% confidence</span>}
      </div>
    </div>
  );
}
